/* eslint-disable @typescript-eslint/no-explicit-any */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { captureSentryException } from '@/integrations/sentry/client';

export interface MonthlyBudget {
  id: string;
  user_id: string;
  master_budget_id: string;
  title: string;
  description: string | null;
  year: number;
  month: number;
  created_at: string;
  updated_at: string;
}

export interface MonthlyBudgetItem {
  id: string;
  monthly_budget_id: string;
  category_id: string;
  sub_category_id: string;
  amount: number;
  currency: string;
  description: string | null;
  created_at: string;
  updated_at: string;
}

// ---------------------------------------------------------------------------
// Monthly Budget queries
// ---------------------------------------------------------------------------

export function useMonthlyBudgets() {
  const { user } = useAuth();
  return useQuery({
    queryKey: ['monthly-budgets', user?.id],
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from('monthly_budgets')
        .select('*')
        .order('year', { ascending: false })
        .order('month', { ascending: false });
      if (error) throw error;
      return data as MonthlyBudget[];
    },
    enabled: !!user,
  });
}

export function useMonthlyBudget(id: string | undefined) {
  return useQuery({
    queryKey: ['monthly-budgets', id],
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from('monthly_budgets')
        .select('*')
        .eq('id', id!)
        .single();
      if (error) throw error;
      return data as MonthlyBudget;
    },
    enabled: !!id,
  });
}

// ---------------------------------------------------------------------------
// Monthly Budget mutations
// ---------------------------------------------------------------------------

export function useCreateMonthlyBudget() {
  const qc = useQueryClient();
  const { user } = useAuth();
  return useMutation({
    mutationFn: async (input: {
      master_budget_id: string;
      title: string;
      description?: string;
      year: number;
      month: number;
      items?: Array<{
        category_id: string;
        sub_category_id: string;
        amount: number;
        currency: string;
        description?: string;
      }>;
    }) => {
      const { items, ...budgetFields } = input;

      const { data: newBudget, error: budgetError } = await (supabase as any)
        .from('monthly_budgets')
        .insert({ ...budgetFields, user_id: user!.id })
        .select()
        .single();
      if (budgetError) throw budgetError;

      if (items && items.length > 0) {
        const { error: itemsError } = await (supabase as any)
          .from('monthly_budget_items')
          .insert(items.map((item) => ({ ...item, monthly_budget_id: newBudget.id })));
        if (itemsError) throw itemsError;
      }

      return newBudget as MonthlyBudget;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['monthly-budgets'] });
      toast.success('Monthly budget created successfully');
    },
    onError: (err: any) => {
      const message = err?.code === '23505'
        ? 'A monthly budget for that month already exists'
        : (err?.message ?? 'Failed to create monthly budget');
      toast.error(message);
      captureSentryException(err);
    },
  });
}

export function useUpdateMonthlyBudget() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...input }: { id: string; title: string; description?: string }) => {
      const { data, error } = await (supabase as any)
        .from('monthly_budgets')
        .update({ ...input, updated_at: new Date().toISOString() })
        .eq('id', id)
        .select()
        .single();
      if (error) throw error;
      return data as MonthlyBudget;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['monthly-budgets'] });
      toast.success('Monthly budget updated successfully');
    },
    onError: (err: any) => {
      toast.error(err?.message ?? 'Failed to update monthly budget');
      captureSentryException(err);
    },
  });
}

export function useDeleteMonthlyBudget() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await (supabase as any)
        .from('monthly_budgets')
        .delete()
        .eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['monthly-budgets'] });
      toast.success('Monthly budget deleted');
    },
    onError: (err: any) => {
      toast.error(err?.message ?? 'Failed to delete monthly budget');
      captureSentryException(err);
    },
  });
}

// ---------------------------------------------------------------------------
// Monthly Budget Item queries & mutations
// ---------------------------------------------------------------------------

export function useMonthlyBudgetItems(monthlyBudgetId: string | undefined) {
  return useQuery({
    queryKey: ['monthly-budget-items', monthlyBudgetId],
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from('monthly_budget_items')
        .select('*')
        .eq('monthly_budget_id', monthlyBudgetId!)
        .order('created_at');
      if (error) throw error;
      return data as MonthlyBudgetItem[];
    },
    enabled: !!monthlyBudgetId,
  });
}

export function useAddMonthlyBudgetItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (item: Omit<MonthlyBudgetItem, 'id' | 'created_at' | 'updated_at'>) => {
      const { data, error } = await (supabase as any)
        .from('monthly_budget_items')
        .insert(item)
        .select()
        .single();
      if (error) throw error;
      return data as MonthlyBudgetItem;
    },
    onSuccess: (_data: any, variables: any) => {
      qc.invalidateQueries({ queryKey: ['monthly-budget-items', variables.monthly_budget_id] });
      toast.success('Budget item added');
    },
    onError: (err: any) => {
      const message = err?.code === '23505'
        ? 'That category/sub-category combination already exists in this budget'
        : (err?.message ?? 'Failed to add budget item');
      toast.error(message);
      captureSentryException(err);
    },
  });
}

export function useUpdateMonthlyBudgetItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      monthlyBudgetId,
      ...input
    }: {
      id: string;
      monthlyBudgetId: string;
      amount?: number;
      currency?: string;
      description?: string;
    }) => {
      const { data, error } = await (supabase as any)
        .from('monthly_budget_items')
        .update({ ...input, updated_at: new Date().toISOString() })
        .eq('id', id)
        .select()
        .single();
      if (error) throw error;
      return data as MonthlyBudgetItem;
    },
    onSuccess: (_data: any, variables: any) => {
      qc.invalidateQueries({ queryKey: ['monthly-budget-items', variables.monthlyBudgetId] });
      toast.success('Budget item updated');
    },
    onError: (err: any) => {
      toast.error(err?.message ?? 'Failed to update budget item');
      captureSentryException(err);
    },
  });
}

export function useDeleteMonthlyBudgetItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, monthlyBudgetId }: { id: string; monthlyBudgetId: string }) => {
      const { error } = await (supabase as any)
        .from('monthly_budget_items')
        .delete()
        .eq('id', id);
      if (error) throw error;
      return monthlyBudgetId;
    },
    onSuccess: (_data: any, variables: any) => {
      qc.invalidateQueries({ queryKey: ['monthly-budget-items', variables.monthlyBudgetId] });
      toast.success('Budget item removed');
    },
    onError: (err: any) => {
      toast.error(err?.message ?? 'Failed to remove budget item');
      captureSentryException(err);
    },
  });
}
